import "./banking.css";
import { get } from "lodash";
import { dispatch } from "store";
import T from "utils/constants/T";
import { useSelector } from "react-redux";
import {
  UpdateCompanyLender,
  fetchLendersDetailedList,
} from "store/reducers/companyBankingSettings";
import EditBankingTable from "./EditBankingTable";
import { decryptBankingData } from "utils/helper";
import { Typography, Button, Box } from "@mui/material";
import CashDDialog from "components/common/CashDDialog";
import React, { useEffect, useReducer, useMemo, useCallback } from "react";
import CorporateCustomerTermsAndConditionsModal from "./CorporateCustomerTermsAndConditionsModal";

interface ChangeLenderDetailsProps {
  selectedCompanyId?: string;
  handleLenderChangeModal?: () => void;
}

const ChangeLenderDetails: React.FC<ChangeLenderDetailsProps> = ({
  selectedCompanyId = "",
  handleLenderChangeModal = () => {},
}) => {
  const { niumTermsAcceptedLenderIds } = useSelector(
    (state: any) => state.companyTabData
  );

  const { isProduction } = useSelector(
    (state: any) => state.dashboardSearch
  );

  // Local state management using useReducer
  const [localState, setLocalState] = useReducer(
    (prevState: any, newState: any) => ({ ...prevState, ...newState }),
    {
      openLenderChangeConfirmationModal: false,
      newLenderId: "",
      newLenderAccountName: "",
      openTermAndConditionsModal: false,
    }
  );

  const {
    newLenderId,
    newLenderAccountName,
    openTermAndConditionsModal,
    openLenderChangeConfirmationModal,
  } = localState;

  // Selectors for state
  const { lendersList, paymentPartner } = useSelector(
    (state: any) => state.bankingSettingsTabData
  );

  // Memoized table row data computation
  const allTableRows = useMemo(() => {
    return lendersList.map((row: any) => {
      const creditVal = get(row, "total_amount", 0);
      const debitVal = get(row, "total_deduction", 0);
      const balanceVal = creditVal - debitVal;
      const iRateVal = get(row, "interest_rate_value", "");
      const iRateType =
        get(row, "interest_rate_type", "") === T.DOLLARTEXT
          ? T.DOLLAR
          : T.PERCENTAGE;
      const finalInterestRate =
        iRateType === T.DOLLAR
          ? `${iRateType}${iRateVal}`
          : `${iRateVal}${iRateType}`;

      let accountName, bsb, accountNumber;
      if (paymentPartner === T.MONOOVA) {
        accountName = get(row, isProduction ? "live_receivables_account_name" : "test_receivables_account_name", "")
        bsb = decryptBankingData(get(row, isProduction ? "live_receivables_account_bsb" : "test_receivables_account_bsb", ""));
        accountNumber = decryptBankingData(
          get(row, isProduction ? "live_receivables_account_number" : "test_receivables_account_number", "")
        );
      } else if (paymentPartner === T.NIUM) {
        accountName = get(row, "lender_name", "");
        accountNumber = get(row, isProduction ? "nium_live_account_number" : "nium_sandbox_account_number", "");
      }

      return {
        lenderId: get(row, "_id", ""),
        accountName,
        fundingType: get(row, "funding_type", ""),
        nameOfBank: paymentPartner,
        ...(bsb && { bsb }),
        accountNumber,
        balance: balanceVal,
        interestRate: finalInterestRate,
      };
    });
  }, [lendersList, paymentPartner]);

  // Fetch lenders list on component mount or when selectedCompanyId changes
  useEffect(() => {
    dispatch(fetchLendersDetailedList(selectedCompanyId));
  }, [selectedCompanyId]);

  // Update lender function
  const lenderUpdate = useCallback(
    (newLenderId: string) => {
      const payload = {
        newLenderId,
        companyId: selectedCompanyId,
        providerType: paymentPartner,
      };
      dispatch(UpdateCompanyLender(payload));
    },
    [selectedCompanyId, paymentPartner]
  );

  // Handle adding a new lender
  const handleAddChangeLender = useCallback(
    (newLenderId: string, newLenderAccountName: string) => {
      setLocalState({ newLenderId, newLenderAccountName });
      if (
        paymentPartner !== "NIUM" ||
        (paymentPartner === "NIUM" &&
          niumTermsAcceptedLenderIds.includes(newLenderId))
      ) {
        handleLenderChangeConfirmationModal(true);
      } else {
        handleTermsAndConditionsModal();
      }
    },
    [niumTermsAcceptedLenderIds, paymentPartner]
  );

  // Toggle lender change confirmation modal
  const handleLenderChangeConfirmationModal = useCallback(
    (isOpen) => {
      setLocalState({
        openTermAndConditionsModal: false,
        openLenderChangeConfirmationModal: isOpen,
      });
    },
    [localState]
  );

  // Confirm lender change
  const handleChangeLenderConfirmation = useCallback(() => {
    lenderUpdate(newLenderId);
    handleLenderChangeConfirmationModal(true);
    handleLenderChangeModal();
  }, [
    lenderUpdate,
    newLenderId,
    handleLenderChangeConfirmationModal,
    handleLenderChangeModal,
  ]);

  // Handle Submit button click
  const handleTermsAndConditionsModal = useCallback(() => {
    setLocalState({
      openTermAndConditionsModal: !localState.openTermAndConditionsModal,
      openLenderChangeConfirmationModal: false,
    });
  }, [localState]);

  return (
    <>
      <EditBankingTable
        allTableRows={allTableRows}
        handleAddChangeLender={handleAddChangeLender}
      />

      {openTermAndConditionsModal && (
        <CorporateCustomerTermsAndConditionsModal
          open={openTermAndConditionsModal}
          lenderId={newLenderId}
          lenderName={newLenderAccountName}
          onClose={handleTermsAndConditionsModal}
          openLenderConfirmationModal={handleLenderChangeConfirmationModal}
        />
      )}
      <CashDDialog
        open={openLenderChangeConfirmationModal}
        handleClose={() => handleLenderChangeConfirmationModal(false)}
      >
        <Typography>
          {`${T.ARE_YOU_SURE_YOU_WANT_TO_ADD} ${newLenderAccountName}`}
        </Typography>
        <Box className="confirmation_modal">
          <Button
            variant="outlined"
            onClick={() => handleLenderChangeConfirmationModal(false)}
          >
            {T.NO}
          </Button>
          <Button variant="contained" onClick={handleChangeLenderConfirmation}>
            {T.YES}
          </Button>
        </Box>
      </CashDDialog>
    </>
  );
};

export default React.memo(ChangeLenderDetails);
